// @ts-nocheck
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Row, Table } from "react-bootstrap-v5";
import Select from "react-select";
import Swal from "sweetalert2";
import { getRequestOptions, postRequestOptions } from "../helpers/Fetchwrapper";
import showLoader from "../helpers/ShowLoader";

interface SelectOption {
    value: number;
    label: string;
}

interface LedgerNode {
    id: number;
    ledgerName: string;
    ledgerCode: string;
    ledgerType: string;
    drAmount?: number | null;
    crAmount?: number | null;
    children?: LedgerNode[];
}

interface EntryValue {
    debit: string;
    credit: string;
}

const INITIAL_ENTRY: EntryValue = { debit: "", credit: "" };

const tableWrapperStyle = {
    maxHeight: "65vh",
    overflowY: "auto",
};

const stickyHeaderCellStyle = {
    position: "sticky",
    top: 0,
    backgroundColor: "#f1f3f5",
    zIndex: 2,
};

const tableCellStyle = {
    padding: "0.35rem 0.5rem",
    verticalAlign: "middle",
};

const menuPortalTarget = typeof document !== "undefined" ? document.body : undefined;

const selectPortalStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
};

const OpeningBalanceEntry = () => {
    const [projects, setProjects] = useState<SelectOption[]>([]);
    const [selectedProject, setSelectedProject] = useState<SelectOption | null>(null);
    const [ledgerTree, setLedgerTree] = useState<LedgerNode[]>([]);
    const [entryMap, setEntryMap] = useState<Record<number, EntryValue>>({});

    useEffect(() => {
        fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedProject) {
            fetchOpeningBalance(selectedProject.value);
        } else {
            setLedgerTree([]);
            setEntryMap({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProject]);

    const fetchProjects = async () => {
        showLoader();
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}projects`, getRequestOptions());
            const data = await response.json();
            Swal.close();

            if (data.status) {
                const options: SelectOption[] = (data.data || []).map((project: any) => ({
                    value: project.id_projects,
                    label: project.project_name,
                }));
                setProjects(options);
                if (!selectedProject && options.length > 0) {
                    setSelectedProject(options[0]);
                }
            } else {
                Swal.fire("Error", "Unable to load projects.", "error");
            }
        } catch (error) {
            Swal.close();
            Swal.fire("Error", "An unexpected error occurred while loading projects.", "error");
        }
    };

    const fetchOpeningBalance = async (projectId: number) => {
        showLoader();
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}opening-balance/${projectId}`, getRequestOptions());
            const data = await response.json();
            Swal.close();

            if (data.success) {
                const nodes: LedgerNode[] = data.data?.ledgerTree || [];
                setLedgerTree(nodes);
                const initialEntries = buildEntryMap(nodes);
                setEntryMap(initialEntries);
            } else {
                setLedgerTree([]);
                setEntryMap({});
                Swal.fire("Error", data.message || "Unable to load opening balance data.", "error");
            }
        } catch (error) {
            Swal.close();
            setLedgerTree([]);
            setEntryMap({});
            Swal.fire("Error", "An unexpected error occurred while loading opening balances.", "error");
        }
    };

    const buildEntryMap = (nodes: LedgerNode[], accumulator: Record<number, EntryValue> = {}) => {
        nodes.forEach((node) => {
            if (node.ledgerType?.toLowerCase() === "gl") {
                accumulator[node.id] = {
                    debit: node.drAmount !== null && node.drAmount !== undefined && node.drAmount !== 0 ? node.drAmount.toString() : "",
                    credit: node.crAmount !== null && node.crAmount !== undefined && node.crAmount !== 0 ? node.crAmount.toString() : "",
                };
            }

            if (node.children && node.children.length > 0) {
                buildEntryMap(node.children, accumulator);
            }
        });

        return accumulator;
    };

    const handleProjectChange = (option: SelectOption | null) => {
        setSelectedProject(option);
    };

    const handleAmountChange = (ledgerId: number, field: "debit" | "credit", value: string) => {
        setEntryMap((prev) => {
            const updatedValue = { ...(prev[ledgerId] || INITIAL_ENTRY), [field]: value };
            return { ...prev, [ledgerId]: updatedValue };
        });
    };

    const totals = useMemo(() => {
        let debit = 0;
        let credit = 0;

        Object.values(entryMap).forEach(({ debit: debitValue, credit: creditValue }) => {
            debit += parseFloat(debitValue || "0") || 0;
            credit += parseFloat(creditValue || "0") || 0;
        });

        return { debit, credit, difference: debit - credit };
    }, [entryMap]);

    const isBalanced = useMemo(() => Math.abs(totals.difference) < 0.005, [totals.difference]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!selectedProject) {
            Swal.fire("Validation", "Please select a project first.", "warning");
            return;
        }

        // if (!isBalanced) {
        //     Swal.fire("Validation", "Total debit and credit must be equal before submitting.", "warning");
        //     return;
        // }

        const entries = Object.entries(entryMap)
            .map(([ledgerId, value]) => {
                const debit = parseFloat(value.debit || "0") || 0;
                const credit = parseFloat(value.credit || "0") || 0;
                return {
                    ledgerId: Number(ledgerId),
                    debit,
                    credit,
                };
            })
            .filter((entry) => entry.debit > 0 || entry.credit > 0);

        showLoader();

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}opening-balance`, postRequestOptions({
                projectId: selectedProject.value,
                entries,
            }));
            const data = await response.json();
            Swal.close();

            if (data.success) {
                Swal.fire("Success", data.message || "Opening balances saved successfully.", "success");
                fetchOpeningBalance(selectedProject.value);
            } else {
                Swal.fire("Error", data.message || "Unable to save opening balances.", "error");
            }
        } catch (error) {
            Swal.close();
            Swal.fire("Error", "An unexpected error occurred while saving opening balances.", "error");
        }
    };

    const renderRows = (nodes: LedgerNode[], level = 0) =>
        nodes.map((node) => {
            const indentStyle = { paddingLeft: `${level * 20}px` };
            const isGroup = node.ledgerType?.toLowerCase() === "group";
            const entry = entryMap[node.id] || INITIAL_ENTRY;

            return (
                <Fragment key={node.id}>
                    <tr className={isGroup ? "table-light" : ""}>
                        <td style={tableCellStyle}>
                            <span style={indentStyle} className={isGroup ? "fw-bold" : ""}>
                                {node.ledgerName}
                            </span>
                        </td>
                        <td style={tableCellStyle} className={isGroup ? "fw-bold" : ""}>{node.ledgerCode}</td>
                        <td style={tableCellStyle} className={isGroup ? "fw-bold text-uppercase" : "text-uppercase"}>{node.ledgerType}</td>
                        <td style={tableCellStyle}>
                            {isGroup ? (
                                "-"
                            ) : (
                                <Form.Control
                                    type="number"
                                    min="0"
                                    step="0.0001"
                                    size="sm"
                                    className="text-end"
                                    value={entry.debit}
                                    onChange={(event) => handleAmountChange(node.id, "debit", event.target.value)}
                                />
                            )}
                        </td>
                        <td style={tableCellStyle}>
                            {isGroup ? (
                                "-"
                            ) : (
                                <Form.Control
                                    type="number"
                                    min="0"
                                    step="0.0001"
                                    size="sm"
                                    className="text-end"
                                    value={entry.credit}
                                    onChange={(event) => handleAmountChange(node.id, "credit", event.target.value)}
                                />
                            )}
                        </td>
                    </tr>
                    {node.children && node.children.length > 0 && renderRows(node.children, level + 1)}
                </Fragment>
            );
        });

    return (
        <Card>
            <Card.Header>
                <Card.Title className="mb-0">Opening Balance Entry</Card.Title>
            </Card.Header>
            <Card.Body>
                <Form onSubmit={handleSubmit}>
                    <Row className="mb-4">
                        <Col md={4}>
                            <Form.Label className="fw-bold mb-2">Project</Form.Label>
                            <Select
                                value={selectedProject}
                                options={projects}
                                onChange={handleProjectChange}
                                placeholder="Select a project"
                                menuPortalTarget={menuPortalTarget}
                                styles={selectPortalStyles}
                                isClearable
                            />
                        </Col>
                        <Col md={{ span: 4, offset: 4 }} className="d-flex flex-column align-items-end">
                            <div className="fw-semibold">Total Debit: {totals.debit.toFixed(2)}</div>
                            <div className="fw-semibold">Total Credit: {totals.credit.toFixed(2)}</div>
                            <div className={`fw-semibold ${isBalanced ? "text-success" : "text-danger"}`}>
                                Difference: {totals.difference.toFixed(2)}
                            </div>
                        </Col>
                    </Row>

                    <div className="table-responsive" style={tableWrapperStyle}>
                        <Table bordered hover size="sm" className="align-middle">
                            <thead className="table-secondary">
                                <tr>
                                    <th style={{ ...stickyHeaderCellStyle, textAlign: "left", ...tableCellStyle }}>Ledger Name</th>
                                    <th style={{ ...stickyHeaderCellStyle, textAlign: "left", ...tableCellStyle }}>Ledger Code</th>
                                    <th style={{ ...stickyHeaderCellStyle, textAlign: "left", ...tableCellStyle }}>Ledger Type</th>
                                    <th style={{ ...stickyHeaderCellStyle, textAlign: "right", ...tableCellStyle }}>Debit</th>
                                    <th style={{ ...stickyHeaderCellStyle, textAlign: "right", ...tableCellStyle }}>Credit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ledgerTree.length > 0 ? (
                                    renderRows(ledgerTree)
                                ) : (
                                    <tr>
                                        <td colSpan={5} className="text-center py-5">
                                            {selectedProject ? "No ledger data available." : "Please select a project to view ledgers."}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr className="fw-bold">
                                    <td style={tableCellStyle} colSpan={3} className="text-end">
                                        Totals
                                    </td>
                                    <td style={{ ...tableCellStyle, textAlign: "right" }}>{totals.debit.toFixed(2)}</td>
                                    <td style={{ ...tableCellStyle, textAlign: "right" }}>{totals.credit.toFixed(2)}</td>
                                </tr>
                            </tfoot>
                        </Table>
                    </div>

                    <div className="d-flex justify-content-end">
                        <Button type="submit" variant="primary" disabled={!selectedProject}>
                            Save Opening Balances
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default OpeningBalanceEntry;

